<template>
  <div>
    <div
      v-if="showPreviewTemplateImage"
      class="flex justify-center items-center d-theme-dark-bg border-solid
      d-theme-border-grey-light border-2 h-32">
      <feather-icon
        icon="ImageIcon"
        svgClasses=" text-grey stroke-current w-16 h-16" />
    </div>

    <div
      v-if="valueLocal"
      class="preview-image relative flex justify-center items-center">
      <feather-icon
        v-if="showRemoveImage"
        class="remove-icon bg-white rounded-full p-1 border"
        icon="XIcon"
        svgClasses="text-grey-dark stroke-current w-16 h-16"
        @click="removeImage"/>

      <img
        :src="valueLocal"
        alt="alternative text"
        :width="previewImageSize"
        height="auto"
        class="rounded-md">
    </div>

    <upload-file
      v-if="!minimalist || !valueLocal"
      ref="uploadFile"
      :key="uploadFileKey"
      :class="[onLoadFile ? 'opacity-2' : '', minimalist ? '' : 'mt-5']"
      :file-type="$t('$General.Image')"
      :accept-ext="acceptExt"
      :file-input-name="fileInputName"
      :max-file-size="maxFileSize"
      :minimal="minimalist"
      :show-only-icon="showOnlyIcon"
      :upload-icon="uploadIcon"
      :upload-icon-size="uploadIconSize"
      @file="uploadToS3">
    </upload-file>

    <upload-loader v-show="onLoadFile"></upload-loader>

    <div
      v-if="!minimalist"
      class="flex flex-col md:flex-row justify-between text-grey-dark text-sm">
      <span>
        {{ $t('$Components.$UploaderPreviewImage.MaxImageSizeMsg') }}
      </span>
      <span>
        {{ $t(
        '$Components.$UploaderPreviewImage.ValidImageFormats',
        { formats: acceptExt.toString().replaceAll(',', ', ') })
        }}
      </span>
    </div>
  </div>
</template>

<script>
import { getExtFromFileType } from '@/util';
import { uploadFileS3 } from '@/aws';
import { v4 as uuidv4 } from 'uuid';
import UploadFile from '@/views/modules/_components/UploadFile.vue';
import UploadLoader from '@/views/modules/_components/UploadLoader.vue';

/**
 * Component for upload and view images
 *
 * @module views/modules/components/UploaderViewImage
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'UploaderViewImage',
  components: {
    UploadFile,
    UploadLoader,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    basePath: {
      type: String,
      required: true,
    },
    fileName: {
      type: String,
      required: false,
      default: uuidv4(),
    },
    fileInputName: {
      type: String,
      required: false,
      default: 'File',
    },
    minimalist: {
      type: Boolean,
      default: false,
    },
    showOnlyIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    uploadIcon: {
      type: String,
      required: false,
      default: 'UploadCloudIcon',
    },
    uploadIconSize: {
      type: Number,
      required: false,
      default: 16,
    },
    previewImageSize: {
      type: String,
      required: false,
      default: '300',
    },
    showRemoveImage: {
      type: Boolean,
      required: false,
      default: false,
    },
    acceptExt: {
      type: Array,
      required: false,
      default: () => ['.jpg', '.jpeg', '.png', '.svg'],
      validator(extensions) {
        let valid = true;
        const patter = /\.([a-zA-Z]|[0-9])+/;

        extensions.forEach((ext) => {
          if (typeof ext !== 'string' || !patter.test(ext)) {
            valid = false;
          }
        });

        return valid;
      },
    },
  },
  data() {
    return {
      valueLocal: this.value,
      onLoadFile: false,
      uploadFileKey: 0,
      maxFileSize: 5120,
    };
  },
  computed: {
    showPreviewTemplateImage() {
      return !this.valueLocal && !this.minimalist;
    },
  },
  watch: {
    value(val) {
      this.valueLocal = val;
    },
  },
  methods: {
    removeImage() {
      this.valueLocal = '';
      this.$emit('input', this.valueLocal);
    },
    async uploadToS3(file) {
      if (file && file.type) {
        this.onLoadFile = true;
        const ext = getExtFromFileType(file.type);
        const resp = await uploadFileS3(file, `${this.basePath}/${this.fileName}.${ext}`);
        console.log(resp);
        this.valueLocal = `${resp.Location}?r=${uuidv4()}`;
        this.$emit('input', this.valueLocal);
        this.onLoadFile = false;
        this.uploadFileKey += 1;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.remove-icon {
  cursor: pointer;
  width: 24px;
  height: 24px;
  position: absolute !important;
  top: -10px;
  right: -10px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;
  border: solid 1px rgba(var(--vs-grey-dark), 1);
}

.preview-image:hover .remove-icon {
  opacity: 1;
  visibility: visible;
}
</style>
